import { BrowserBarHint, Button } from '@wundo/ui';
import { browserName } from '@/lib/browserName.ts';
import type { MicrophoneBlock } from '@/lib/microphoneBlock.ts';
import { m } from '@/paraglide/messages.js';
import { microphoneGuide } from './microphoneGuide.tsx';

export interface PracticeMicrophoneBlockedProps {
  block: MicrophoneBlock;
  onRetry: () => void;
}

/**
 * What to do about a refused microphone, for this browser only.
 *
 * A computer that blocks the browser gets one sentence and no steps: those
 * settings belong to the operating system, or to an IT team.
 */
export function PracticeMicrophoneBlocked({
  block,
  onRetry,
}: PracticeMicrophoneBlockedProps) {
  const guide = microphoneGuide(browserName(navigator.userAgent), window.location.host);

  return (
    <div
      role='alert'
      className='flex flex-col gap-3 rounded-md border border-danger-line bg-danger-fill p-4'
    >
      {block === 'system' ? (
        <p className='text-base text-ink'>{m.practice_session_mic_blocked_system()}</p>
      ) : (
        <>
          <p className='text-base font-semibold text-danger'>
            {m.practice_session_mic_blocked_title()}
          </p>
          {guide.marker !== null && (
            <BrowserBarHint marker={guide.marker}>{guide.bar}</BrowserBarHint>
          )}
          <ol className='flex list-decimal flex-col gap-1 pl-5 text-base text-ink'>
            {guide.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </>
      )}

      <div>
        <Button variant='quiet' size='sm' onClick={onRetry}>
          {m.practice_session_mic_blocked_retry()}
        </Button>
      </div>
    </div>
  );
}
